function initPostMaloneToggleHero() {
  // The transition clips run ~4.2s; this is only a backstop for the case where
  // 'ended' never arrives, so keep it just clear of the real clip length.
  const TOGGLE_RELEASE_TIMEOUT_MS = 6000;

  // Backstop for a load that neither succeeds nor errors — a stalled network
  // leaves 'loadeddata' and 'error' both unfired indefinitely.
  const VIDEO_LOAD_TIMEOUT_MS = 8000;

  class PostMaloneToggleHero {
    constructor() {
      this.videoSources = {
        default: {
          webm: 'https://files.backbon3.com/contentful/rv16LfXFNDLlAmvA0Zfic.webm',
          mp4: 'https://files.backbon3.com/contentful/6c80Rqm2C8W4Ch6fQs0chM.mp4',
        },
        lightsOn: {
          webm: 'https://files.backbon3.com/contentful/2Pf0hUpkYCdQzv1Vtzuzm5.webm',
          mp4: 'https://files.backbon3.com/contentful/wwBJrkc18iMnEDF1T6xLF.mp4',
        },
        transition: {
          webm: 'https://files.backbon3.com/contentful/4tuj1Nb2bw8uwNU9GY7Skg.webm',
          mp4: 'https://files.backbon3.com/contentful/3zvZ4SjBewcJ9nzcazV0L6.mp4',
        },
      };
      this.isLightsOn = false;
      // Bumped on every toggle so a slow load belonging to a superseded click
      // cannot come back later and swap the clip out from under the current one.
      this.toggleGeneration = 0;
      this.init();
    }

    init() {
      this.setupToggleCircle();
      this.loadInitialVideo();
      this.setupEventListeners();
    }

    setupToggleCircle() {
      const circle = document.querySelector('.post-melone-toggle-circle');
      if (circle) {
        circle.style.display = 'block';
        circle.style.visibility = 'visible';
        circle.style.opacity = '1';
      }
    }

    loadInitialVideo() {
      const videoElement = document.getElementById('controllerVideo');
      if (videoElement) {
        this.loadVideoSources(videoElement, this.videoSources.default, true);
      }
    }

    setupEventListeners() {
      window.toggleSwitch = (element) => this.toggleSwitch(element);
    }

    // Resolves true once the clip is actually playing, false if playback could not
    // start. Callers use that to decide whether to wait for 'ended' or give up.
    loadVideoSources(videoElement, sources, shouldLoop) {
      if (!videoElement) return Promise.resolve(false);

      videoElement.innerHTML = '';

      const webmSource = document.createElement('source');
      webmSource.src = sources.webm;
      webmSource.type = 'video/webm';
      videoElement.appendChild(webmSource);

      const mp4Source = document.createElement('source');
      mp4Source.src = sources.mp4;
      mp4Source.type = 'video/mp4';
      videoElement.appendChild(mp4Source);

      videoElement.loop = shouldLoop;

      videoElement.load();

      // load() aborts any play() already in flight, so calling play() straight
      // after it rejects with AbortError and leaves the video paused on frame 0.
      // Wait until the new source has data before starting playback.
      if (videoElement.readyState >= 2) {
        return this.startPlayback(videoElement);
      }

      // A source that cannot decode fires 'error' and never 'loadeddata'. That is
      // a live case here, not a theoretical one: the clips are VP9 webm with an
      // HEVC mp4 fallback, and neither decodes everywhere. Resolving only from
      // 'loadeddata' would leave this promise pending forever, and with it the
      // caller's recovery path — including the restore of the looping default
      // clip. Settle on whichever event arrives, or on a timeout if neither does.
      return new Promise((resolve) => {
        let settled = false;
        let timer = null;

        const finish = (playing) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          videoElement.removeEventListener('loadeddata', onLoadedData);
          videoElement.removeEventListener('error', onError);
          resolve(playing);
        };

        const onLoadedData = () => this.startPlayback(videoElement).then(finish);

        // Only the media element's own 'error' lands here — 'error' on a child
        // <source> does not bubble — so this fires once every source has failed.
        const onError = () => {
          console.error('Video source failed to load:', sources.webm);
          finish(false);
        };

        timer = setTimeout(() => finish(false), VIDEO_LOAD_TIMEOUT_MS);

        videoElement.addEventListener('loadeddata', onLoadedData);
        videoElement.addEventListener('error', onError);
      });
    }

    startPlayback(videoElement) {
      return videoElement.play().then(
        () => true,
        (err) => {
          console.error('Video play failed:', err);
          return false;
        }
      );
    }

    toggleSwitch(element) {
      if (!element) return;

      element.style.pointerEvents = 'none';
      const release = this.createReleaser(element);
      const generation = ++this.toggleGeneration;
      const isCurrent = () => generation === this.toggleGeneration;

      element.classList.toggle('active');
      this.isLightsOn = element.classList.contains('active');

      // Let the hero backdrop respond to the switch. Without this nothing outside
      // the video and the heading glow changes, so flipping the lights left the
      // page looking identical.
      const heroContainer = element.closest('.post-malone-hero-container');
      if (heroContainer) {
        heroContainer.classList.toggle('is--lights-out', this.isLightsOn);
      }

      const text = element.previousElementSibling;
      if (text) {
        text.textContent = this.isLightsOn ? 'Lights on' : 'Lights off';
      }

      const circle = element.querySelector('.post-melone-toggle-circle');
      if (circle) {
        circle.style.display = 'block';
        circle.style.visibility = 'visible';
        circle.style.opacity = '1';
      }

      const spans = document.querySelectorAll('.post-melone-neon-heading span');
      if (spans && spans.length) {
        spans.forEach((span) => {
          span.classList.toggle('active', this.isLightsOn);
        });
      }

      const video = document.getElementById('controllerVideo');
      if (!video) {
        release();
        return;
      }

      if (this.isLightsOn) {
        video.onended = () => {
          video.onended = null;
          release();
        };

        this.loadVideoSources(video, this.videoSources.lightsOn, false).then((playing) => {
          if (!playing && isCurrent()) release();
        });
      } else {
        video.onended = () => {
          video.onended = null;
          this.loadVideoSources(video, this.videoSources.default, true);
          release();
        };

        this.loadVideoSources(video, this.videoSources.transition, false).then((playing) => {
          if (!playing && isCurrent()) {
            this.loadVideoSources(video, this.videoSources.default, true);
            release();
          }
        });
      }
    }

    // The switch is disabled while a one-shot clip plays and re-enabled when it
    // ends. Guard that with a timeout as well: a browser can report playback as
    // started and still never advance a frame (the clips are VP9 with an HEVC
    // fallback, neither of which decodes everywhere), in which case 'ended' never
    // fires and the switch would otherwise stay dead for the rest of the session.
    createReleaser(element) {
      let released = false;
      let timer = null;

      const release = () => {
        if (released) return;
        released = true;
        if (timer) clearTimeout(timer);
        element.style.pointerEvents = 'auto';
      };

      timer = setTimeout(release, TOGGLE_RELEASE_TIMEOUT_MS);

      return release;
    }
  }

  return new PostMaloneToggleHero();
}
document.addEventListener('DOMContentLoaded', function () {
  if (document.getElementById('controllerVideo') && document.querySelector('.post-melone-toggle-circle')) {
    initPostMaloneToggleHero();
  }
});
